import { SearchOutlined } from '@mui/icons-material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ClearIcon from '@mui/icons-material/Clear'; // Import du logo pour effacer
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, CircularProgress, FormControl, InputAdornment, MenuItem, Select, TextField, Typography } from '@mui/material';

import GridIcon2 from 'assets/GridIcon2';
import ListIcon from 'assets/ListIcon';
import ButtonIcon from 'components/ButtonIcon';
import ButtonIconSvg from 'components/ButtonIconSvg';
import ButtonIconText from 'components/ButtonIconText';
import Restrict from 'components/Restrict';
import UseAuthContext from 'context/AuthContext';
import LanguageContext from 'context/LanguageContext';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { exportProjectsCSV } from 'services/project-service';

const ProjectFilter = ({ changeView, handleSearch, handleClearSearch, view}: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { businessId } = useParams();
  const [value, setValue] = useState("");
  const [searchType, setSearchType] = useState("projectName");
  const { language } = useContext(LanguageContext);
  const { role, organizations } = useContext(UseAuthContext);


  const handleChangeSearchText = (e: any) => {
    setValue(e.target.value);
    handleSearch(e.target.value, searchType);
  };

  const handleSearchTypeChange = (e: any) => {
    setSearchType(e.target.value);
  };

  const handleClear = () => {
    setValue("");
    handleClearSearch();
  };

  // React Query mutation for CSV export
  const exportCSVMutation = useMutation(async () => {
    return await exportProjectsCSV(businessId);
  });

  const handleDownloadCSV = async () => {
    try {
      const blob = await exportCSVMutation.mutateAsync();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${organizations.company_name}_projects.csv`);

      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Optionally show error
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: "144px",
        mb: "22px",
        flexWrap: "wrap",
        gap: '1rem'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '2rem',
          flexWrap: "wrap",
          flex: 1
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '24px',
              lineHeight: '31px',
              color: '#2F3B49',
            }}
          >
            {t('PROJECTS_TITLE')}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'nowrap'
        }}>
          <Restrict forbidden={['viewer']}>
            <ButtonIconText
              icon={<div style={{ fontSize: "40px", display: "flex", alignItems: "center" }}><AddRoundedIcon fontSize='inherit' /></div>}
              disableElevation={true}
              type='submit'
              variant='contained'
              size='large'
              onClick={(e: any) => {
                e.preventDefault();
                const url = `${businessId ? '/add-project/' + businessId : '/add-project'}`;
                navigate(url);
              }}>
              {t("PROJECTS_ADD_BUTTON")}
            </ButtonIconText>
          </Restrict>
        
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '0.5rem',
              '& > *': {
                cursor: 'pointer',
              },
            }}
          >
            <ButtonIconSvg isActive={view === 'Grid'} onClick={() => changeView('Grid')}>
              <GridIcon2 />
            </ButtonIconSvg>
            <ButtonIconSvg isActive={view === 'Table'} onClick={() => changeView('Table')}>
              <ListIcon />
            </ButtonIconSvg>
          </Box>
        </Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap',
          width: { xs: '100%', md: '50%', lg: '50%' }
        }}>
          <TextField
          
            type='text'
            fullWidth
            variant='outlined'
            placeholder={t('PROJECTS_SEARCH_PLACEHOLDER')}
            sx={{
              width: { md: '100%', lg: '590px' },
              
              '& .MuiInputBase-root': {
                WebkitFontSmoothing: 'auto',
                padding: "0px",
              
              },
              '& .MuiInputBase-inputAdornedStart': {
                padding: 0,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FormControl sx={{
                    
                  }}>
                    <Select
                      value={searchType}
                      onChange={handleSearchTypeChange}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root': {
                                 fontSize: '16px',
                              },
                          },
                        },
                      }}
                      sx={{
                        height: '50px',
                        width: language === 'he' ? '90px' : '109px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '4px 0px 0px 4px',
                        borderRight: '1px solid #C4C4C4',

                        '& .MuiSelect-select': {
                          padding: '8px 10px 8px 8px',
                          fontSize: '16px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none',
                        },
                      }}
                    >
                      <MenuItem value="projectName">{t('PROJECTS_SEARCH_BY_NAME')}</MenuItem>
                      <MenuItem value="projectClientName">{t('PROJECTS_SEARCH_BY_CLIENT_NAME')}</MenuItem>
                      <MenuItem value="projectType">{t('PROJECTS_SEARCH_BY_TYPE')}</MenuItem>
                      <MenuItem value="projectClimate">{t('PROJECTS_SEARCH_BY_CLIMATE')}</MenuItem>
                      <MenuItem value="projectCity">{t('PROJECTS_SEARCH_BY_CITY')}</MenuItem>
                      <MenuItem value="projectVersion">{t('PROJECTS_SEARCH_BY_VERSION')}</MenuItem>
                    </Select>
                  </FormControl>
                  {value ? (
                    <ButtonIcon onClick={handleClear}>
                      <ClearIcon />
                    </ButtonIcon>
                  ) : (
                    <ButtonIcon disabled>
                      <SearchOutlined />
                    </ButtonIcon>
                  )}
                </InputAdornment>
              ),
              sx: { height: '50px' }
            }}
            value={value}
            onChange={handleChangeSearchText}
            autoComplete='off'
          />
        </Box>
      </Box>
        {/* Only admin can see the download button */}
        {role === 'admin' && (
              <ButtonIcon
                onClick={handleDownloadCSV}
                disabled={exportCSVMutation.isLoading}
              >
                {exportCSVMutation.isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <FileDownloadOutlinedIcon fontSize='large' />
                )}
              </ButtonIcon>
          )}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.5rem',
          '& > *': {
            cursor: 'pointer',
          },
        }}
      >
        <ButtonIconSvg isActive={view === 'Grid'} onClick={() => changeView('Grid')}>
          <GridIcon2 />
        </ButtonIconSvg>
        <ButtonIconSvg isActive={view === 'Table'} onClick={() => changeView('Table')}>
          <ListIcon />
        </ButtonIconSvg>
      </Box>
    </Box>
  );
};

export default ProjectFilter;